import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { Button, Spin } from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { platformIntl } from '../../../../constants';
import { VerifyStatus } from '../../../../interfaces';
import hourglassEmpty from '../../../../assets/empty/hourglass-empty.png';
import lockEmpty from '../../../../assets/empty/lock-empty.png';
import { SubmittedAuthType, type AuthTarget, type BlockKind } from '../../types';

interface VerificationGuardStatus {
    corp?: VerifyStatus;
    owner?: VerifyStatus;
    verified: boolean;
    blockKind: BlockKind;
    submittedAuthType?: SubmittedAuthType;
    nextAuthTarget: AuthTarget;
    canVerify: boolean;
}

interface VerificationGuardPanelProps {
    className?: string;
    size?: 'small' | 'medium' | 'large';
    helpUrl?: string;
    loading?: boolean;
    pollingInterval?: number;
    status: VerificationGuardStatus;
    onVerify?: (target?: AuthTarget) => void;
    onRefresh?: () => void;
}

const getAuthTypeText = (type?: SubmittedAuthType) => {
    switch (type) {
        case SubmittedAuthType.Corp:
            return platformIntl.get('fxp.base.bg.corp_info.corp_auth.opt');
        case SubmittedAuthType.Owner:
            return platformIntl.get('fxp.base.bg.corp_info.personal_auth.opt');
        case SubmittedAuthType.Identity:
        default:
            return platformIntl.get('fxp.base.bg.corp_info.identity_auth.nm');
    }
};

const getActionText = (target: AuthTarget) => {
    switch (target) {
        case 'corp':
            return platformIntl.get('fxp.base.bg.corp_info.corp_auth.btn');
        case 'owner':
            return platformIntl.get('fxp.base.bg.corp_info.personal_auth.btn');
        case 'select':
        default:
            return platformIntl.get('fxp.base.bg.corp_info.auth_now.btn');
    }
};

/**
 * 租户认证准入阻断面板，用于展示未认证或审核中的内容区空态。
 */
const VerificationGuardPanel: FC<VerificationGuardPanelProps> = ({
    className,
    size = 'medium',
    helpUrl,
    loading,
    pollingInterval,
    status,
    onVerify,
    onRefresh
}) => {
    const [pollingEnabled, setPollingEnabled] = useState(false);
    const isReviewing = status.blockKind === 'reviewing';
    const allowPolling = status.nextAuthTarget !== 'corp';

    useEffect(() => {
        if (
            status.verified ||
            !allowPolling ||
            !pollingEnabled ||
            !pollingInterval ||
            pollingInterval <= 0 ||
            !onRefresh
        ) {
            return;
        }

        const timer = setInterval(onRefresh, pollingInterval);

        return () => {
            clearInterval(timer);
        };
    }, [allowPolling, onRefresh, pollingEnabled, pollingInterval, status.verified]);

    const handleVerify = () => {
        if (allowPolling) {
            setPollingEnabled(true);
        }
        onVerify?.(status.nextAuthTarget);
    };

    if (status.verified) {
        return null;
    }

    const panelClassName = classNames(
        'fx-verification-guard-panel',
        `size-${size}`,
        { reviewing: isReviewing },
        className
    );

    if (loading) {
        return (
            <div className={panelClassName}>
                <div className="verification-body">
                    <Spin />
                </div>
            </div>
        );
    }

    if (isReviewing) {
        const authType = getAuthTypeText(status.submittedAuthType);
        const showVerifyLink =
            status.canVerify && status.submittedAuthType !== SubmittedAuthType.Identity;

        return (
            <div className={panelClassName}>
                <div className="verification-body">
                    <img className="verification-image" src={hourglassEmpty} alt="" />
                    <div className="verification-title">
                        {platformIntl.get('fxp.base.bg.corp_info.unusable_wait_auth_review.tip')}
                    </div>
                    <div className="verification-description">
                        {platformIntl.get(
                            status.canVerify
                                ? 'fxp.base.bg.corp_info.auth_submitted_wait_review.tip'
                                : 'fxp.base.bg.corp_info.auth_submitted_wait_review_admin.tip',
                            { authtype: authType }
                        )}
                    </div>
                </div>
                {(helpUrl || showVerifyLink) && (
                    <div className="verification-footer">
                        {helpUrl && (
                            <Hyperlink
                                className="verification-link"
                                underline
                                href={helpUrl}
                                target="jdy_doc"
                            >
                                {platformIntl.get('fxp.learn_more.btn')}
                            </Hyperlink>
                        )}
                        {showVerifyLink && (
                            <Hyperlink
                                className="verification-link"
                                underline={false}
                                href="#"
                                target="_blank"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    handleVerify();
                                }}
                            >
                                {getActionText(status.nextAuthTarget)}
                            </Hyperlink>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={panelClassName}>
            <div className="verification-body">
                <img className="verification-image" src={lockEmpty} alt="" />
                <div className="verification-title">
                    {platformIntl.get('fxp.base.bg.corp_info.identity_auth_to_use_function.tip')}
                </div>
                <div className="verification-description">
                    <span>
                        {platformIntl.get(
                            status.canVerify
                                ? 'fxp.base.bg.corp_info.auth_for_security_compliance.tip'
                                : 'fxp.base.bg.corp_info.auth_for_security_admin.tip'
                        )}
                    </span>
                    {helpUrl && (
                        <Hyperlink
                            className="verification-help"
                            underline
                            href={helpUrl}
                            target="jdy_doc"
                        >
                            {platformIntl.get('fxp.learn_more.btn')}
                        </Hyperlink>
                    )}
                </div>
                {status.canVerify && (
                    <Button type="primary" className="verification-action" onClick={handleVerify}>
                        {getActionText(status.nextAuthTarget)}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default VerificationGuardPanel;
